import {
    ClipSize16And9RatioId,
    ClipSize1And1RatioId,
    ClipSize9And16RatioId,
    ClipSizeToWidthAndHeight
} from "../../utils/utils";
import {MediaSourceRef} from "../../engine/sources/media-source";
import {ImageClip} from "../../engine/clips/image/image";
import {CompositionStateStore} from "../../librettoEditor/store/CompositionStateStore";
import {ALL_FORMATS, Input, UrlSource} from "mediabunny";
import {VideoClip} from "../../engine/clips/video/video";
import {AudioClip} from "../../engine/clips";
import {Composition} from "../../engine/composition";
import {SequenceStateStore} from "../../sequenceEditor/store/SequenceStateStore";
import {ScriptLayerClip} from "../../engine/clips/script/script";
import type {ScriptLayerInputConfig} from "../../engine/clips/script/script.interfaces";
import {Timestamp} from "../../engine/models";
import { compositionHasAnyVisual, fitToCanvas, maybeLockCanvasToFirstVideo } from "../../utils/canvasSizing";


type StateStore = typeof CompositionStateStore | typeof SequenceStateStore;

/**
 * Create a ScriptLayerClip from pre-built input configurations.
 * 
 * This is the central function for creating script clips, used for:
 * - Inserting sequences into the script layer
 * - Inserting individual files into the script layer  
 * - Restoring script clips from persistence
 * 
 * @param inputConfigs - Array of ScriptLayerInputConfig (sources with their settings)
 * @param options - Configuration options
 * @returns Initialized ScriptLayerClip ready to be added to the composition
 */
const CreateScriptClipForComposition = async ({
    inputConfigs,
    composition,
    stateStore,
    clipId,
    clipName,
    range,
}: {
    inputConfigs: ScriptLayerInputConfig[];
    composition: Composition;
    stateStore: StateStore;
    clipId?: string;
    clipName?: string;
    range?: { inSec: number; outSec: number };
}): Promise<ScriptLayerClip | null> => {
    try {
        if (!inputConfigs || inputConfigs.length === 0) {
            console.warn('[CreateScriptClipForComposition] No input configs provided');
            return null;
        }

        // Get composition dimensions for positioning
        const compWidth = composition?.width ?? 1920;
        const compHeight = composition?.height ?? 1080;

        // Create ScriptLayerClip from the input configs
        const scriptLayerClip = ScriptLayerClip.fromInputConfigs(inputConfigs, {
            position: { x: compWidth / 2, y: compHeight / 2 },
            width: compWidth,
            height: compHeight,
        } as any, clipId);

        // Set name for the clip
        if (clipName) {
            scriptLayerClip.name = clipName;
            stateStore.setTitleForClip(scriptLayerClip.id, clipName);
        }

        // Set asset IDs in state store for each source (for waveform lookup, etc.)
        for (const config of inputConfigs) {
            if (config.assetId) {
                stateStore.setAssetIdForClip(scriptLayerClip.id, config.assetId);
                // Note: Only the last assetId will be stored per clipId
                // This is expected for single-source clips; multi-source clips
                // may need additional handling in the future
            }
        }

        // Initialize the clip (sets duration, creates sinks, etc.)
        await scriptLayerClip.init();

        console.log('[CreateScriptClipForComposition] After init - duration:', scriptLayerClip.duration?.seconds);

        // Set range if provided, otherwise default to full duration
        if (range && range.inSec !== undefined && range.outSec !== undefined) {
            scriptLayerClip.range = [Timestamp.fromSeconds(range.inSec), Timestamp.fromSeconds(range.outSec)];
        } else if (scriptLayerClip.duration && scriptLayerClip.duration.seconds > 0) {
            scriptLayerClip.range = [new Timestamp(), scriptLayerClip.duration.copy()];
        }

        console.log('[CreateScriptClipForComposition] Created script clip:', scriptLayerClip.id, {
            name: clipName,
            sources: inputConfigs.length,
            duration: scriptLayerClip.duration?.seconds,
        });

        return scriptLayerClip;
    } catch (e) {
        console.error('[CreateScriptClipForComposition] Failed to create script layer clip:', e);
        return null;
    }
};

const CreateClipForComposition = async ({id, type, assetId, assetTitle, assetResolution, composition, stateStore, resolveAssetUrl, thumbnailId} : {id?: string, type: string, assetId: string, assetTitle: string, assetResolution: string, composition: Composition, stateStore: StateStore, thumbnailId?: string,  resolveAssetUrl: (assetId: string, type: 'audio'|'video'|'image') => Promise<string | undefined>}) => {
    try {
        if (type === 'image') {
            const resolutionMatch = assetResolution.match(/(\d+)x(\d+)/);

            let trackWidth = 0;
            let trackHeight = 0;
            if (resolutionMatch) {
                trackWidth = parseInt(resolutionMatch[1]);
                trackHeight = parseInt(resolutionMatch[2]);
            }

            // Classify source orientation (fallback to landscape if unknown)
            let aspectId = ClipSize16And9RatioId;
            if (trackWidth > 0 && trackHeight > 0) {
                if (trackWidth > trackHeight) aspectId = ClipSize16And9RatioId;
                else if (trackWidth < trackHeight) aspectId = ClipSize9And16RatioId;
                else aspectId = ClipSize1And1RatioId;
            }

            // If this is the first VISUAL clip in the composition, orient the canvas to match.
            // (If a ScriptClip already has video sources, do NOT re-orient here.)
            if (!compositionHasAnyVisual(composition)) {
                // TODO: The composition canvas resolution should be fixed (modulo the vertical, horizontal and square re-orientations.)
                // This resize should actually just be a reorientation of the canvas, not a resize.
                const preset = ClipSizeToWidthAndHeight(aspectId);
                composition.resize(preset.width, preset.height);
            }

            const canvasWidth = composition?.width ?? 1920;
            const canvasHeight = composition?.height ?? 1080;
            const clipProps: any = { position: {x: canvasWidth / 2, y: canvasHeight / 2} };
            clipProps.width = canvasWidth;
            clipProps.height = canvasHeight;

            const url = await resolveAssetUrl(assetId, 'image');
            const ref: MediaSourceRef = { id: assetId, kind: 'image', imgUrl: url };
            const clip = new ImageClip(ref, clipProps, id);
            clip.name = assetTitle;
            stateStore.setAssetIdForClip(clip.id, assetId);
            stateStore.setThumbnailIdForClip(clip.id, thumbnailId);
            stateStore.setOriginalMediaResolutionForClip(clip.id, trackWidth, trackHeight);
            stateStore.setTitleForClip(clip.id, assetTitle);
            return clip;
        }

        const resolutionMatch = assetResolution.match(/(\d+)x(\d+)/);
        const isVideo = type === 'video';
        let trackWidth = 0;
        let trackHeight = 0;
        if (resolutionMatch && isVideo) {
            trackWidth = parseInt(resolutionMatch[1]);
            trackHeight = parseInt(resolutionMatch[2]);
        }

        const kind: 'audio' | 'video' = isVideo ? 'video' : 'audio';
        // @ts-ignore - extended signature supports hydration metadata
        const url = await resolveAssetUrl(assetId, kind);
        if (!url) return;
        const source = new UrlSource(url);
        const input = new Input({ source, formats: ALL_FORMATS });
        const ref: MediaSourceRef = { id: assetId, kind, input };

        let clip;
        if (isVideo) {
            // First VIDEO sets the composition canvas resolution (and thus max export resolution).
            if (trackWidth > 0 && trackHeight > 0) {
                maybeLockCanvasToFirstVideo(composition, trackWidth, trackHeight);
            }

            const canvasWidth = composition?.width ?? 1920;
            const canvasHeight = composition?.height ?? 1080;

            // Size the clip to FIT inside the canvas without distortion.
            const fitted =
                trackWidth > 0 && trackHeight > 0
                    ? fitToCanvas(trackWidth, trackHeight, canvasWidth, canvasHeight)
                    : { width: canvasWidth, height: canvasHeight };

            const clipProps: any = { position: {x: canvasWidth / 2, y: canvasHeight / 2} };
            clipProps.width = fitted.width;
            clipProps.height = fitted.height;
            clip = new VideoClip(ref, clipProps, id);
        } else {
            clip = new AudioClip(ref, {}, id);
        }


        clip.name = assetTitle;
        stateStore.setAssetIdForClip(clip.id, assetId);
        stateStore.setOriginalMediaResolutionForClip(clip.id, trackWidth, trackHeight);
        stateStore.setTitleForClip(clip.id, assetTitle);
        return clip;
    } catch (e) {
        console.error('Failed to add to composition', e);
    }
};

export {CreateClipForComposition, CreateScriptClipForComposition};
